import React, { useState, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import { useAuth } from '../hooks/useAuth';
import {
  subscribeUsers, subscribePendingInvites,
  createPendingInvite, deactivateUser, logActivity,
} from '../firebase/firestore';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export default function AdminUsers() {
  const { profile } = useAuth();
  const [users,          setUsers]         = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [search,         setSearch]        = useState('');
  const [roleFilter,     setRoleFilter]    = useState('all');
  const [showInvite,     setShowInvite]    = useState(false);
  const [inviteEmail,    setInviteEmail]   = useState('');
  const [inviteRole,     setInviteRole]    = useState('staff');
  const [inviteName,     setInviteName]    = useState('');
  const [sending,        setSending]       = useState(false);
  const [sent,           setSent]          = useState(false);

  useEffect(() => {
    const u1 = subscribeUsers(setUsers);
    const u2 = subscribePendingInvites(d => setPendingInvites(d));
    return () => { u1(); u2(); };
  }, []);

  const visible = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (search && !`${u.name||''} ${u.email||''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const changeRole = async (userId, newRole, userName) => {
    // Optimistic update
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      await logActivity({
        userId:   profile?.uid, userName: profile?.name,
        action:   'update', entityType: 'user', entityId: userId,
        message:  `Changed ${userName}'s role to ${newRole}`,
      });
    } catch (_) {
      // Roll back on failure
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: u.role } : u));
    }
  };

  const removeUser = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName}? They will no longer have access to the system.`)) return;
    try {
      await deactivateUser(userId);
      await logActivity({
        userId:   profile?.uid, userName: profile?.name,
        action:   'delete', entityType: 'user', entityId: userId,
        message:  `Removed ${userName} from the team`,
      });
    } catch (e) {
      alert('Failed to remove user. Please try again.');
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSending(true);
    try {
      await createPendingInvite(inviteEmail.trim(), inviteRole, profile?.uid, profile?.name);
      await logActivity({
        userId:   profile?.uid, userName: profile?.name,
        action:   'create', entityType: 'user', entityId: inviteEmail.trim(),
        message:  `Invited ${inviteEmail.trim()} as ${inviteRole}`,
      });
      setSent(true);
      setTimeout(() => {
        setSent(false); setShowInvite(false);
        setInviteEmail(''); setInviteName(''); setInviteRole('staff');
      }, 2200);
    } catch (e) {
      alert('Failed to create invite. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const initials = name => name?.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';
  const joined   = u => u.createdAt?.toDate ? u.createdAt.toDate().toLocaleDateString('en-GB',{month:'short',year:'numeric'}) : u._date || '—';

  const adminCount = users.filter(u => u.role === 'admin').length;
  const staffCount = users.filter(u => u.role === 'staff').length;

  const myId = profile?.uid;

  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Manage users</div>
            <div className="page-sub">{users.length} total · {adminCount} admin · {staffCount} staff</div>
          </div>
          <button className="btn primary" onClick={() => setShowInvite(true)}>
            <i className="ti ti-user-plus" /> Invite user
          </button>
        </div>
        <div className="scroll">

          {/* Filter bar */}
          <div style={{ display:'flex', gap:8, marginBottom:20, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ position:'relative' }}>
              <i className="ti ti-search" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--gray-400)', fontSize:14 }} />
              <input type="text" placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)}
                style={{ padding:'8px 12px 8px 32px', borderRadius:8, border:'1px solid var(--gray-200)', fontSize:13, width:220, outline:'none' }} />
            </div>
            <div style={{ display:'flex', gap:4 }}>
              {['all','admin','staff'].map(r => (
                <button key={r} onClick={() => setRoleFilter(r)} style={{
                  padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer',
                  border:'1px solid '+(roleFilter===r ? 'var(--blue)' : 'var(--gray-200)'),
                  background: roleFilter===r ? 'var(--blue-light)' : '#fff',
                  color: roleFilter===r ? 'var(--blue)' : 'var(--gray-500)',
                }}>
                  {r === 'all' ? 'All' : r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>
            <span style={{ fontSize:12, color:'var(--gray-400)', marginLeft:'auto' }}>{visible.length} result{visible.length !== 1 ? 's' : ''}</span>
          </div>

          {/* User cards */}
          {visible.length === 0
            ? <div className="empty-state"><i className="ti ti-users" /><p>No users found.</p></div>
            : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
              {visible.map(u => {
                const uid    = u.uid || u.id;
                const isMe   = uid === myId;
                return (
                  <div key={u.id} className="card" style={{ marginBottom:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
                      <div className="avatar" style={{ background: u.role==='admin' ? 'var(--blue)' : 'var(--purple)', width:44, height:44, fontSize:14, fontWeight:700, flexShrink:0 }}>
                        {u.photoURL
                          ? <img src={u.photoURL} alt="" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }} />
                          : initials(u.name)}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:600, fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {u.name || 'Unnamed user'}{isMe ? ' (you)' : ''}
                        </div>
                        <div style={{ fontSize:12, color:'var(--gray-500)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</div>
                      </div>
                      <span className={`badge ${u.role}`}>{u.role}</span>
                    </div>

                    <div style={{ fontSize:11, color:'var(--gray-400)', marginBottom:12 }}>
                      <i className="ti ti-calendar" style={{ marginRight:4 }} />Joined {joined(u)}
                    </div>

                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, paddingTop:10, borderTop:'1px solid var(--gray-200)' }}>
                      <span style={{ fontSize:12, color:'var(--gray-500)' }}>Role</span>
                      <div className="btn-group">
                        <select
                          value={u.role}
                          onChange={e => changeRole(uid, e.target.value, u.name)}
                          disabled={isMe}
                          style={{ fontSize:12, padding:'5px 8px', borderRadius:6, border:'1px solid var(--gray-200)', background:'#fff', cursor: isMe ? 'not-allowed' : 'pointer', outline:'none' }}
                        >
                          <option value="staff">Staff</option>
                          <option value="admin">Admin</option>
                        </select>
                        {!isMe && (
                          <button className="btn sm danger" onClick={() => removeUser(uid, u.name)} title="Remove user">
                            <i className="ti ti-user-minus" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>}

          {/* Pending invites section */}
          {pendingInvites.length > 0 && (
            <div style={{ marginTop:24 }}>
              <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'var(--gray-700)', display:'flex', alignItems:'center', gap:8 }}>
                <i className="ti ti-mail-forward" style={{ color:'var(--amber)' }} />
                Pending invites
                <span style={{ fontSize:11, fontWeight:400, color:'var(--gray-500)' }}>— waiting for the invitee to sign in</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:10 }}>
                {pendingInvites.map(inv => (
                  <div key={inv.id} className="card" style={{ marginBottom:0, borderLeft:'3px solid var(--amber)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div className="avatar" style={{ background:'var(--amber)', fontSize:10, width:36, height:36 }}>
                        {inv.email.slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{inv.email}</div>
                        <div style={{ fontSize:11, color:'var(--gray-500)' }}>
                          Invited as <strong>{inv.role}</strong> by {inv.invitedByName || 'admin'}
                        </div>
                      </div>
                      <span className="badge pending">Pending</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Invite modal */}
      {showInvite && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowInvite(false)}>
          <div className="modal" style={{ maxWidth:420 }}>
            <div className="modal-header">
              <span className="modal-title"><i className="ti ti-user-plus" style={{ color:'var(--blue)', marginRight:8 }} />Invite team member</span>
              <button className="btn sm" onClick={() => setShowInvite(false)}><i className="ti ti-x" /></button>
            </div>
            {sent
              ? <div className="alert success" style={{ margin:'24px 0' }}>
                  <i className="ti ti-check" /> Invite created for {inviteEmail}. They'll get the assigned role on first Google sign-in.
                </div>
              : <>
                <div className="form-group" style={{ marginBottom:12 }}>
                  <label>Full name (optional)</label>
                  <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="e.g. Grace Apio" />
                </div>
                <div className="form-group" style={{ marginBottom:12 }}>
                  <label>Email address *</label>
                  <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="colleague@example.com" autoFocus />
                </div>
                <div className="form-group" style={{ marginBottom:16 }}>
                  <label>Role</label>
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="alert info" style={{ marginBottom:4 }}>
                  <i className="ti ti-info-circle" />
                  <span>The person must sign in with the exact Google account for this email. Their role is assigned automatically on first login.</span>
                </div>
              </>}
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowInvite(false)}>Cancel</button>
              {!sent && (
                <button className="btn primary" onClick={sendInvite} disabled={sending || !inviteEmail.trim()}>
                  <i className="ti ti-send" /> {sending ? 'Saving…' : 'Create invite'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
